import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { listDepartments } from "../../services/departmentService";
import { listMembers } from "../../services/memberService";

/**
 * DepartmentsContext (F2.4) — diretório leve de atribuição do Centro de
 * Mensagens. Busca UMA vez os setores (`GET /tenants/departments`) e os membros
 * do tenant (`GET /members`) e os expõe como listas + mapas por id.
 *
 * Evita prop-drilling: tanto o badge de setor no `ConversationItem` (fundo da
 * lista) quanto o `AssigneeSelect` (cabeçalho da thread) leem daqui, sem que a
 * `ConversationList` / `ConversationThread` precisem repassar o mapa.
 *
 * Fora de um Provider (ex.: um componente renderizado isolado em teste), o hook
 * degrada para um diretório vazio — nada quebra, o badge simplesmente não
 * aparece.
 */

const EMPTY = {
  departments: [],
  activeDepartments: [],
  departmentsById: {},
  members: [],
  membersById: {},
  loading: false,
  error: false,
  refresh: () => {},
};

const DepartmentsContext = createContext(EMPTY);

export function DepartmentsProvider({ children }) {
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [depRes, memRes] = await Promise.all([
        listDepartments(),
        listMembers(),
      ]);
      setDepartments(Array.isArray(depRes.data) ? depRes.data : []);
      setMembers(Array.isArray(memRes.data) ? memRes.data : []);
    } catch {
      // Falha de rede não trava a inbox: sem diretório, o seletor mostra o
      // estado de carregamento/erro e o badge de setor some.
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    // Mapa por id inclui setores inativos: uma sessão pode estar atribuída a um
    // setor que depois foi desativado — ainda queremos resolver o nome/cor.
    const departmentsById = {};
    for (const d of departments) if (d?.id) departmentsById[d.id] = d;

    const membersById = {};
    for (const m of members) if (m?.id) membersById[m.id] = m;

    // O seletor só oferece setores ativos como destino de atribuição.
    const activeDepartments = departments.filter((d) => d?.isActive);

    return {
      departments,
      activeDepartments,
      departmentsById,
      members,
      membersById,
      loading,
      error,
      refresh,
    };
  }, [departments, members, loading, error, refresh]);

  return (
    <DepartmentsContext.Provider value={value}>
      {children}
    </DepartmentsContext.Provider>
  );
}

DepartmentsProvider.propTypes = {
  children: PropTypes.node,
};

export function useDepartments() {
  return useContext(DepartmentsContext);
}
